/**
 * Height conversion and formatting.
 * Canonical API value is always centimetres; UI uses the user's preferred unit (cm | ft).
 */

const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;
const METERS_PER_INCH = 0.0254;
const METERS_PER_FOOT = METERS_PER_INCH * 12;
const CM_PER_METER = 100;

/** Unit tokens the height screens store; `ft`/`m` are mobile's short form. */
export const HEIGHT_UNIT = { METERS: 'Meters', FEET: 'Feet' } as const;

const isFeetUnit = (unit?: string): boolean =>
  unit === HEIGHT_UNIT.FEET || unit === 'ft';

/** Converts a height typed in `unit` to meters, the value the API is fed from. */
export const toHeightMeters = (value: number, unit?: string): number =>
  isFeetUnit(unit) ? value * METERS_PER_FOOT : value;

/** Converts meters back to the unit currently shown on screen. */
export const fromHeightMeters = (meters: number, unit?: string): number =>
  isFeetUnit(unit) ? meters / METERS_PER_FOOT : meters;

/** Converts a height typed in `unit` to centimeters, the API's field. */
export const toHeightCM = (value: number, unit?: string): number =>
  toHeightMeters(value, unit) * CM_PER_METER;

/** Converts an API centimeter value to the unit currently shown on screen. */
export const fromHeightCM = (cm: number, unit?: string): number =>
  fromHeightMeters(cm / CM_PER_METER, unit);

export type HeightUnitCode = 'cm' | 'ft';

/** OnboardingProfileInfo HeightUnit enum (never send UI labels like "ft" / "cm"). */
export type OnboardingHeightUnit = 'HeightCM' | 'HeightFT';

export const ONBOARDING_HEIGHT_UNIT = {
  CM: 'HeightCM',
  FT: 'HeightFT',
} as const satisfies Record<string, OnboardingHeightUnit>;

/** Backend ScaleUnit lookup — Height rows. */
export const HEIGHT_SCALE_UNIT_ID = {
  CM: 3,
  FT: 4,
} as const;

export const normalizeHeightUnit = (unit?: string | null): HeightUnitCode => {
  const u = String(unit ?? '')
    .toLowerCase()
    .trim();
  if (
    u === 'ft' ||
    u === 'feet' ||
    u === 'in' ||
    u === 'heightft' ||
    u === '4' ||
    u.includes('ft') ||
    u.includes('feet')
  ) {
    return 'ft';
  }
  // UI onboarding uses 'm' for metric height — map to cm for API/display preference.
  return 'cm';
};

/** Map UI height preference → OnboardingProfileInfo HeightUnit enum. */
export const toOnboardingHeightUnit = (
  unit?: string | null,
): OnboardingHeightUnit =>
  normalizeHeightUnit(unit) === 'ft'
    ? ONBOARDING_HEIGHT_UNIT.FT
    : ONBOARDING_HEIGHT_UNIT.CM;

/** Map UI / preference unit → ScaleUnitID (3 = CM, 4 = FT). */
export const toHeightScaleUnitId = (unit?: string | null): number =>
  normalizeHeightUnit(unit) === 'ft'
    ? HEIGHT_SCALE_UNIT_ID.FT
    : HEIGHT_SCALE_UNIT_ID.CM;

/** Convert height in centimetres to `{ feet, inches }`. */
export const cmToFeetInches = (
  heightCm?: number | null,
): { feet: number; inches: number } => {
  if (heightCm == null || !Number.isFinite(heightCm) || heightCm <= 0) {
    return { feet: 0, inches: 0 };
  }
  const totalInches = Math.round(heightCm / CM_PER_INCH);
  const feet = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = totalInches % INCHES_PER_FOOT;
  return { feet, inches };
};

/** Convert feet + inches to centimetres (2 decimal places). */
export const feetInchesToCm = (ft?: number, inches?: number): number => {
  const totalInches = (ft || 0) * INCHES_PER_FOOT + (inches || 0);
  return Number((totalInches * CM_PER_INCH).toFixed(2));
};

/**
 * Convert meters to feet and inches (onboarding stores height in metres).
 */
export const toFeetAndInches = (
  meters?: number | null,
): { ft: number; in: number } => {
  if (meters == null || isNaN(meters)) {
    return { ft: 0, in: 0 };
  }
  const { feet, inches } = cmToFeetInches(meters * 100);
  return { ft: feet, in: inches };
};

/**
 * Convert feet and inches to meters.
 */
export const fromFeetAndInches = (ft?: number, inches?: number): number => {
  const totalInches = (ft || 0) * INCHES_PER_FOOT + (inches || 0);
  return Number((totalInches * METERS_PER_INCH).toFixed(4));
};

/**
 * Resolve any height input to centimetres for API payloads.
 * - metres + unit 'm'/'cm' → cm
 * - metres stored while UI unit is 'ft' (onboarding) → metres * 100
 * - feet/inches pair → cm
 */
export const toHeightCm = (params: {
  metres?: number | null;
  feet?: number | null;
  inches?: number | null;
  unit?: string | null;
}): number => {
  if (params.feet != null || params.inches != null) {
    return feetInchesToCm(params.feet ?? 0, params.inches ?? 0);
  }
  if (params.metres != null && Number.isFinite(params.metres)) {
    // Onboarding always stores the physical height in metres regardless of UI unit.
    return Number((params.metres * 100).toFixed(2));
  }
  return 0;
};

/**
 * Format height for UI from centimetres + preferred unit.
 * Examples: (172.72, 'ft') → "5 ft 8 in"; (170, 'cm') → "170 cm"
 */
export const formatHeightFromCm = (
  heightCm?: number | null,
  unit?: string | null,
): string => {
  if (heightCm == null || !Number.isFinite(Number(heightCm))) return '';
  const u = normalizeHeightUnit(unit);
  if (u === 'ft') {
    const { feet, inches } = cmToFeetInches(Number(heightCm));
    return `${feet} ft ${inches} in`;
  }
  const cm = Number(Number(heightCm).toFixed(0));
  return `${cm} cm`;
};

/**
 * Formats a height in meters based on the selected unit (onboarding UI).
 */
export const formatHeight = (
  meters?: number | null,
  unit?: 'ft' | 'm' | string,
): string => {
  if (meters == null || isNaN(meters)) {
    return '';
  }
  return formatHeightFromCm(meters * 100, unit === 'm' ? 'cm' : unit);
};
